import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { orderAPI, addressAPI, paymentAPI } from '../api/services';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import Loading from '../components/common/Loading';
import './Checkout.css';

export default function Checkout() {
  const { cart, fetchCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const couponCode = location.state?.couponCode || '';

  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showNewAddr, setShowNewAddr] = useState(false);
  const [addrForm, setAddrForm] = useState({ fullName: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: 'India', isDefault: false });
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    addressAPI.getAll().then(res => {
      const addrs = res.data.data || [];
      setAddresses(addrs);
      const def = addrs.find(a => a.isDefault) || addrs[0];
      if (def) setSelectedAddress(def.id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleNewAddress = async (e) => {
    e.preventDefault();
    try {
      const res = await addressAPI.create(addrForm);
      const addr = res.data.data;
      setAddresses(prev => [...prev, addr]);
      setSelectedAddress(addr.id);
      setShowNewAddr(false);
      setAddrForm({ fullName: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', country: 'India', isDefault: false });
      toast.success('Address added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add address');
    }
  };

  const openRazorpay = (order, razorpayOrder) => {
    const options = {
      key: razorpayOrder.keyId,
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency,
      name: 'MiniMom Co',
      description: `Order #${order.orderNumber}`,
      image: '/logo.svg',
      order_id: razorpayOrder.razorpayOrderId,
      handler: async (response) => {
        try {
          await paymentAPI.verifyPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
          await fetchCart();
          toast.success('Payment successful! Order placed.');
          navigate(`/orders/${order.id}`);
        } catch (err) {
          toast.error('Payment verification failed. Contact support.');
          navigate(`/orders/${order.id}`);
        }
      },
      prefill: {
        name: user?.fullName || '',
        email: user?.email || '',
        contact: user?.phone || '',
      },
      theme: { color: '#e91e63' },
      modal: {
        ondismiss: () => {
          toast('Payment cancelled. You can retry from your orders page.', { icon: '⚠️' });
          navigate(`/orders/${order.id}`);
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) { toast.error('Select a shipping address'); return; }
    setPlacing(true);
    try {
      const payload = {
        shippingAddressId: selectedAddress,
        paymentMethod,
        ...(couponCode ? { couponCode } : {})
      };
      const res = await orderAPI.create(payload);
      const order = res.data.data;

      if (paymentMethod === 'ONLINE') {
        const rzpRes = await paymentAPI.createRazorpayOrder(order.id);
        const razorpayOrder = rzpRes.data.data;
        openRazorpay(order, razorpayOrder);
      } else {
        await fetchCart();
        toast.success('Order placed successfully!');
        navigate(`/orders/${order.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order');
    } finally {
      setPlacing(false);
    }
  };

  if (loading) return <Loading />;

  const items = cart?.items || [];
  const subtotal = items.reduce((s, i) => s + i.unitPrice * i.quantity, 0);

  return (
    <div className="checkout-page">
      <h1>Checkout</h1>
      <div className="checkout-layout">
        <div className="checkout-left">
          <section className="checkout-section">
            <h2>Shipping Address</h2>
            <div className="address-list">
              {addresses.map(a => (
                <label key={a.id} className={`address-card ${selectedAddress === a.id ? 'selected' : ''}`}>
                  <input type="radio" name="address" checked={selectedAddress === a.id} onChange={() => setSelectedAddress(a.id)} />
                  <div>
                    <strong>{a.fullName}</strong> <span>{a.phone}</span>
                    <p>{a.addressLine1}{a.addressLine2 && `, ${a.addressLine2}`}</p>
                    <p>{a.city}, {a.state} - {a.postalCode}</p>
                    {a.isDefault && <span className="badge">Default</span>}
                  </div>
                </label>
              ))}
            </div>
            <button className="add-addr-btn" onClick={() => setShowNewAddr(!showNewAddr)}>
              {showNewAddr ? 'Cancel' : '+ Add New Address'}
            </button>
            {showNewAddr && (
              <form className="addr-form" onSubmit={handleNewAddress}>
                <div className="form-row">
                  <input required placeholder="Full Name" value={addrForm.fullName} onChange={e => setAddrForm(f => ({ ...f, fullName: e.target.value }))} />
                  <input required placeholder="Phone" value={addrForm.phone} onChange={e => setAddrForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
                <input required placeholder="Address Line 1" value={addrForm.addressLine1} onChange={e => setAddrForm(f => ({ ...f, addressLine1: e.target.value }))} />
                <input placeholder="Address Line 2 (optional)" value={addrForm.addressLine2} onChange={e => setAddrForm(f => ({ ...f, addressLine2: e.target.value }))} />
                <div className="form-row">
                  <input required placeholder="City" value={addrForm.city} onChange={e => setAddrForm(f => ({ ...f, city: e.target.value }))} />
                  <input required placeholder="State" value={addrForm.state} onChange={e => setAddrForm(f => ({ ...f, state: e.target.value }))} />
                  <input required placeholder="Pincode" value={addrForm.postalCode} onChange={e => setAddrForm(f => ({ ...f, postalCode: e.target.value }))} />
                </div>
                <button type="submit" className="btn btn-primary btn-sm">Save Address</button>
              </form>
            )}
          </section>

          <section className="checkout-section">
            <h2>Payment Method</h2>
            <div className="payment-options">
              <label className={`payment-option ${paymentMethod === 'COD' ? 'selected' : ''}`}>
                <input type="radio" name="payment" value="COD" checked={paymentMethod === 'COD'} onChange={e => setPaymentMethod(e.target.value)} />
                <div><strong>Cash on Delivery</strong><p>Pay when you receive your order</p></div>
              </label>
              <label className={`payment-option ${paymentMethod === 'ONLINE' ? 'selected' : ''}`}>
                <input type="radio" name="payment" value="ONLINE" checked={paymentMethod === 'ONLINE'} onChange={e => setPaymentMethod(e.target.value)} />
                <div><strong>Online Payment</strong><p>Pay via UPI / Card / Net Banking</p></div>
              </label>
            </div>
          </section>
        </div>

        <div className="checkout-summary">
          <h3>Order Summary</h3>
          <div className="os-items">
            {items.map(item => (
              <div key={item.id} className="os-item">
                <span>{item.productName} × {item.quantity}</span>
                <span>₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <hr />
          <div className="os-row"><span>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
          {couponCode && <div className="os-row"><span>Coupon</span><span className="coupon-tag">{couponCode}</span></div>}
          <div className="os-row"><span>Shipping</span><span>Free</span></div>
          <hr />
          <div className="os-row total"><span>Total</span><span>₹{subtotal.toFixed(2)}</span></div>
          <button className="btn btn-primary btn-block" onClick={handlePlaceOrder} disabled={placing}>
            {placing ? 'Placing Order...' : 'Place Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
